import type { InferContractRouterOutputs } from '@orpc/contract'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { useMemo } from 'react'
import { ArtworkVisibilityType } from 'src/services/apollo/generated/graphql'
import { orpc } from './contract'
import {
  type OrpcArtworkPreviewFragment,
  toArtworkPreviewFragment,
} from './utils/typeAdapters'

export type PersonalArtworkSort = 'latest' | 'oldest' | 'mostLiked'
type ArtworkTab = 'artwork' | 'animation'

// `BaseCursorPaging` calls the hook with the relay cursor params it derived
// (`first`/`after` for forward, `last`/`before` for backward) merged with
// the caller-provided `extraArgs` (userId, sort, etc.). The hook dispatches
// to one of three sort-specific oRPC endpoints based on `sort`.
export interface UseListUserArtworksArgs {
  userId?: string
  sort?: PersonalArtworkSort
  visibility?: ArtworkVisibilityType
  type?: ArtworkTab
  first?: number
  after?: string
  last?: number
  before?: string
}

const VISIBILITY_TO_QUERY: Record<ArtworkVisibilityType, 'PRIVATE' | 'PUBLIC'> =
  {
    [ArtworkVisibilityType.Private]: 'PRIVATE',
    [ArtworkVisibilityType.Public]: 'PUBLIC',
  }

// All three sort-specific endpoints share the same output schema, so the
// union of their inferred types narrows to a single shape — no cast needed
// when iterating `data` / `pageInfo` below.
type UserArtworksResponse =
  InferContractRouterOutputs<BaseContract>['user']['getUserArtworksLatest']

interface ConnectionData {
  edges: { node: OrpcArtworkPreviewFragment; cursor: string }[]
  pageInfo: {
    startCursor?: string
    endCursor?: string
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
  totalCount?: number
}

/**
 * SWR-shaped hook backed by the three sort-specific oRPC endpoints
 * (`/v2/user/{userId}/artworks/{latest,oldest,most-liked}`). Each endpoint
 * supports the full relay cursor surface (forward via `first` + `after`,
 * backward via `last` + `before`) so `BaseCursorPaging` can drive prev/next
 * navigation without any direction-specific quirks.
 */
export const useListUserArtworks = (args: UseListUserArtworksArgs) => {
  const {
    userId,
    sort = 'latest',
    visibility,
    type,
    first,
    after,
    last,
    before,
  } = args

  // `userId` is coerced to bigint server-side via `z.coerce.bigint()`, so we
  // pass the raw string from the URL hash through unchanged. The `''`
  // placeholder is never sent — `enabled: !!userId` below gates the query.
  const input = {
    userId: userId ?? '',
    first,
    after,
    last,
    before,
    visibility: visibility ? VISIBILITY_TO_QUERY[visibility] : undefined,
    type,
  }

  const options =
    sort === 'oldest'
      ? orpc.user.getUserArtworksOldest.queryOptions({ input })
      : sort === 'mostLiked'
        ? orpc.user.getUserArtworksMostLiked.queryOptions({ input })
        : orpc.user.getUserArtworksLatest.queryOptions({ input })

  const result = useQuery({
    ...options,
    enabled: !!userId,
    // Keep the previous page's 20 cards on screen while a new cursor is
    // fetching. Without this, `BaseCursorPaging` sees `data === undefined`
    // on cache miss for the new cursor and switches to its empty-state
    // branch — `items.length === 0 && isValidating` → `<LoadingPage />`.
    // That tiny preloader collapses the grid from a ~5 screen-height
    // article list down to a single spinner, the document height shrinks,
    // and the browser clamps the current scrollTop to the new (much
    // smaller) max — which the user sees as the page jumping back to the
    // very top the instant they click "Next" at the bottom. With
    // `keepPreviousData`, react-query returns the old page's response as
    // a placeholder while the next page is in flight, the grid stays
    // mounted at the same height, and the click-position relative to the
    // pagination button is preserved (smooth stale-while-revalidate).
    // Mirrors the same fix on `usePagedListContests` in `contest.ts`.
    placeholderData: keepPreviousData,
    // `liked` + private-artwork visibility depend on the current user — drop
    // cache across login/logout so authed/visitor responses don't leak.
    meta: { dependsOn: ['login'] },
  })

  const data = useMemo<ConnectionData | undefined>(() => {
    if (!result.data) return undefined
    const response: UserArtworksResponse = result.data
    return {
      edges: response.data.map(item => ({
        node: toArtworkPreviewFragment(item),
        // Per-item cursor isn't surfaced by the endpoint; use the artwork id
        // as a stable identifier. `BaseCursorPaging` reads only the
        // connection-level `pageInfo` for prev/next.
        cursor: item.id,
      })),
      pageInfo: {
        startCursor: response.pageInfo.startCursor,
        endCursor: response.pageInfo.endCursor,
        hasNextPage: response.pageInfo.hasNextPage,
        hasPreviousPage: response.pageInfo.hasPreviousPage,
      },
      totalCount: response.totalCount,
    }
  }, [result.data])

  return {
    data,
    // `BaseCursorPaging` reads this as "isValidating". TanStack Query sets
    // `isLoading` only on the very first fetch; a refetch triggered by
    // `pagingRef.current?.mutate()` (e.g. after an artwork edit / moderation
    // action) flips `isFetching` instead. OR them so the paging UI shows the
    // loading state in both situations.
    loading: result.isLoading || result.isFetching,
    refetch: result.refetch,
    error: result.error,
  }
}
